// @ts-check
const { Decimal } = require('decimal.js');
Decimal.set({ precision: 34, rounding: Decimal.ROUND_HALF_EVEN });
/**
 * Takes in any type of input and checks if it can be used as a Decimal
 * @param {*} value
 * @returns
 */
const isValidDecimal = (value) => {
  // Using try/catch to cover all instances of invalid values for Decimal library
  try {
    const dec = new Decimal(value);

    return !dec.isNaN();
  } catch (error) {
    console.log(error);
    return false;
  }
};

/**
 * @description Returns custom label or default fee label. Provides client friendly fee labels.
 *
 * @param {FeeName} feeName
 * @param {DatabaseFeeSettings} feeSettings
 * @returns {string}
 */
const getCustomLabel = (feeName, feeSettings) => {
  if (!feeName) return '';

  const mapper = {
    tax: 'Sales Tax',
    surcharge: 'Surcharge Fee',
    cardUpcharge: feeSettings?.virtualTerminalSettings?.DiscountFeeName || 'SVC Fee',
    lateFee: feeSettings?.invoiceSettings?.CustomFeeOneName || 'Late Fee',
    cashDiscount: 'ACH Discount',
  };

  return mapper[feeName] ?? '';
};

/**
 * @description util to limit percentage fees to 5 decimal places
 *
 * @param {string} percentStr
 * @returns {string}
 */
function getPercentDecimalPlaces(percentStr) {
  if (!new Decimal(percentStr).isNaN()) {
    const parts = percentStr.split('.');
    if (parts.length === 2 && parts[1].length > 5) {
      return new Decimal(percentStr).toFixed(5);
    }
  }

  return percentStr;
}

/**
 * Formats numeric string to currency string without dollar sign or negative sign. Removes leading and trailing zeros past precision.
 * @param {string} amount
 * @returns {string} cleaned amount
 */
const formatCurrencyInput = (amount) => {
  if (!amount || !isValidDecimal(amount)) return '';
  return new Decimal(amount).abs().toFixed(2);
};

/**
 * @description formats numeric string to dollar currency strings.
 *
 * @param {string | undefined} amount - '1234.00'
 * @returns {string} '$1,234.00' | '-$1,234.00'
 */
const formatCurrency = (amount) => {
  if (!amount || !isValidDecimal(amount)) return '$0.00';
  const amountNum = new Decimal(amount);
  const absAmount = amountNum.abs().toFixed(2);

  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  const formattedNum = formatter.format(Number.parseFloat(absAmount));
  return amountNum.lessThan('0') ? `-${formattedNum}` : `${formattedNum}`;
};

/**
 * @description formats percent and dollar values for a FeeDisplayField label.
 *
 * Example output:
 * '5%'
 * '3.95%'
 * '$5.00'
 * '$125.72'
 *
 * @param {import('../../types/fees').FeeType} feeType
 * @param {string} storedValue - the percentage rate or dollar amount of a fee.
 * @param {string | undefined} feeName - used to determine if it's a discount and needs a negative sign
 * @returns {string} formatted string
 */
const formatFeeType = (feeType, storedValue, feeName = '') => {
  const mapper = {
    '%': () => {
      return `${getPercentDecimalPlaces(storedValue)}%`;
    },
    $: () => {
      return `$${storedValue}`;
    },
  };

  let mapped = mapper[feeType]();

  if (feeName === 'cashDiscount') {
    mapped = `-${mapped}`;
  }
  return mapped;
};

/**
 * @description enforces correct display order for fees.
 *
 * Tax last puts tax last, tax last disabled puts tax first.
 *
 * @param {Fee[]} fees
 * @param {FeeOptions} options
 * @returns {Fee[]}
 */
const orderFeesPostCalculation = (fees, options) => {
  const feeArray = Object.values(fees).map((f) => f);

  let idx;
  feeArray.forEach((fee, i) => {
    if (fee?.name === 'tax') {
      idx = i;
    }
  });

  if (typeof idx === 'number') {
    const taxFee = feeArray.splice(idx, 1)[0];
    if (options.taxLast || options.dualPricing) {
      feeArray.push(taxFee);
    } else {
      feeArray.unshift(taxFee);
    }
  }

  return feeArray;
};

/**
 * @param {Array<string | Object>} list
 * @param {string | null | undefined} fieldName - if adding a list of objects, add name of field to sum
 * @returns {string} sum
 */
const sumList = (list, fieldName = null) => {
  let runningSum = new Decimal('0');
  if (!list) {
    return runningSum.toFixed(2).toString();
  }

  if (typeof fieldName === 'string' && typeof list[0] === 'object') {
    for (const item of list) {
      // @ts-ignore - checks to make sure fieldName is string
      const value = item[fieldName];
      if (isValidDecimal(value)) {
        runningSum = runningSum.plus(new Decimal(value));
      }
    }
  } else if (typeof list[0] === 'string') {
    for (const value of list) {
      if (isValidDecimal(value)) {
        // @ts-ignore
        runningSum = runningSum.plus(new Decimal(value));
      }
    }
  }

  return runningSum.toFixed(2).toString();
};

/**
 *
 * @param {string} minuend
 * @param {string} subtrahend
 * @return {string} difference
 */
const subtractAmounts = (minuend, subtrahend) => {
  if (!isValidDecimal(minuend) || !isValidDecimal(subtrahend)) {
    throw new Error('Cannot complete subtraction.');
  }

  const minuendDec = new Decimal(minuend);
  const subtrahendDec = new Decimal(subtrahend);

  return minuendDec.minus(subtrahendDec).toFixed(2).toString();
};

/**
 * Returns 1 if firstNumber is greater than the secondNumber
 * Returns 0 if the two numbers are equal
 * Returns -1 if firstNumber is less than the secondNumber
 * @param {string} firstNumber
 * @param {string} secondNumber
 * @returns {number}
 */
const compareAmounts = (firstNumber, secondNumber) => {
  // @ts-ignore - isNaN takes a number
  if (!isValidDecimal(firstNumber) || !isValidDecimal(secondNumber)) {
    throw new Error('Cannot compare amounts.');
  }
  const num1 = new Decimal(firstNumber);
  const num2 = new Decimal(secondNumber);
  return num1.cmp(num2);
};

/**
 * @param {string | number} unitCost
 * @param {number} quantity
 * @returns {string} product line amount
 */
const calculateProductLineAmount = (unitCost, quantity) => {
  if (typeof quantity !== 'number' || !isValidDecimal(unitCost)) {
    return '0.00';
  }

  const decimalUnitCost = new Decimal(unitCost);
  return decimalUnitCost.times(quantity).toFixed(2).toString();
};

/**
 * @description Handle potential NaN intermediary input values like '5.' as '5.00' or '.' as '0.00'
 *
 * @param {string} amount
 */
const formatPotentialNaN = (amount) => {
  amount = amount ? amount.toString().trim() : '0.00';

  if (amount.includes('.')) {
    let [num, dec] = amount.split('.');

    if (dec.length === 1) {
      dec = `${dec}0`;
    }

    if (dec.length > 2) {
      dec = dec.slice(0, 2);
    }

    dec = dec || '00';
    num = num || '0';
    amount = `${num}.${dec}`;
  } else {
    amount = `${amount}.00`;
  }

  return amount;
};

module.exports = {
  isValidDecimal,
  formatPotentialNaN,
  getCustomLabel,
  formatCurrency,
  formatFeeType,
  orderFeesPostCalculation,
  getPercentDecimalPlaces,
  sumList,
  subtractAmounts,
  compareAmounts,
  calculateProductLineAmount,
  formatCurrencyInput,
};

/**
 * @typedef {import('../../types/fees').Fee} Fee
 * @typedef {import('../../types/fees').FeeOptions} FeeOptions
 * @typedef {import('../../types/fees').DatabaseFeeSettings} DatabaseFeeSettings
 * @typedef {import('../../types/fees').FeeName} FeeName
 */
